import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  Banknote,
  Check,
  CreditCard,
  Landmark,
  Loader2,
  LogOut,
  MapPin,
  PawPrint,
  Phone,
  RefreshCw,
  X,
} from "lucide-react";
import {
  actualizarSolicitud,
  asegurarAdmin,
  listarSolicitudes,
  type SolicitudRow,
} from "@/lib/panel.functions";
import { formatMXN, formatSlotLabel } from "@/lib/pricing";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/panel")({
  head: () => ({
    meta: [
      { title: "Panel de viajes — PetMoon" },
      {
        name: "description",
        content: "Panel privado del equipo PetMoon para aceptar, rechazar y dar seguimiento a las solicitudes de viaje.",
      },
      { property: "og:title", content: "Panel de viajes — PetMoon" },
      { property: "og:description", content: "Gestión de solicitudes de viaje PetMoon." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PanelPage,
});

const pagoLabel: Record<string, { label: string; icon: typeof Banknote }> = {
  efectivo: { label: "Efectivo / Terminal al abordar", icon: Banknote },
  transferencia: { label: "Transferencia SPEI / Mercado Pago", icon: Landmark },
  tarjeta: { label: "Tarjeta de crédito o débito", icon: CreditCard },
};

function PanelPage() {
  const navigate = useNavigate();
  const cargar = useServerFn(listarSolicitudes);
  const actuar = useServerFn(actualizarSolicitud);
  const bootstrap = useServerFn(asegurarAdmin);

  const [rows, setRows] = useState<SolicitudRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await cargar({});
      setRows(data);
      setError(null);
    } catch {
      setError("No pudimos cargar las solicitudes. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  }, [cargar]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        await bootstrap({});
      } catch {
        /* ya existe un admin */
      }
      if (alive) await refresh();
    })();
    const id = window.setInterval(() => void refresh(), 15000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [bootstrap, refresh]);

  const run = async (id: string, accion: "aceptar" | "rechazar" | "verificar_pago") => {
    setBusyId(id);
    try {
      await actuar({ data: { id, accion } });
      await refresh();
    } catch {
      setError("No se pudo actualizar la solicitud.");
    } finally {
      setBusyId(null);
    }
  };

  const salir = async () => {
    await supabase.auth.signOut();
    void navigate({ to: "/auth", replace: true });
  };

  const pendientes = rows.filter((r) => r.estado_solicitud === "pendiente");
  const resto = rows.filter((r) => r.estado_solicitud !== "pendiente");

  return (
    <main className="min-h-screen bg-secondary/40 px-5 py-10 font-sans">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-primary">
              Solicitudes de viaje
            </h1>
            <p className="mt-1 text-sm font-semibold text-muted-foreground">
              {pendientes.length} pendiente{pendientes.length === 1 ? "" : "s"} de aceptar
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void refresh()}
              className="inline-flex items-center gap-2 rounded-full bg-card px-4 py-2.5 text-sm font-extrabold text-primary ring-1 ring-border"
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
              Actualizar
            </button>
            <button
              type="button"
              onClick={() => void salir()}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-extrabold text-muted-foreground hover:text-primary"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Salir
            </button>
          </div>
        </div>

        {error && (
          <p className="mt-6 rounded-2xl bg-destructive/10 px-4 py-3 text-sm font-bold text-destructive">
            {error}
          </p>
        )}

        {loading ? (
          <p className="mt-10 flex items-center gap-2 text-sm font-bold text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Cargando solicitudes…
          </p>
        ) : rows.length === 0 ? (
          <p className="mt-10 rounded-[2rem] bg-card px-6 py-12 text-center text-sm font-bold text-muted-foreground shadow-card ring-1 ring-border">
            Aún no hay solicitudes de viaje.
          </p>
        ) : (
          <div className="mt-8 space-y-5">
            {[...pendientes, ...resto].map((r) => {
              const pago = pagoLabel[r.metodo_pago] ?? pagoLabel['efectivo']!;
              const PagoIcon = pago.icon;
              const pendiente = r.estado_solicitud === "pendiente";
              return (
                <article
                  key={r.id}
                  className={cn(
                    "rounded-[1.75rem] bg-card p-6 shadow-card ring-1 ring-border",
                    !pendiente && "opacity-80",
                  )}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="font-display text-xl font-bold text-foreground">
                        {r.cliente_nombre || "Cliente PetMoon"}
                      </h2>
                      <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
                        <PawPrint className="h-4 w-4 text-gold-deep" aria-hidden />
                        {r.nombre_mascota} · {r.tipo_mascota} · {r.tamano}
                      </p>
                      {r.cliente_telefono && (
                        <a
                          href={`https://wa.me/52${r.cliente_telefono.replace(/\D/g, "").slice(-10)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-flex items-center gap-1.5 text-sm font-extrabold text-whatsapp"
                        >
                          <Phone className="h-3.5 w-3.5" aria-hidden />
                          {r.cliente_telefono}
                        </a>
                      )}
                    </div>
                    <span
                      className={cn(
                        "rounded-full px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-widest",
                        r.estado_solicitud === "pendiente" && "bg-gold-soft text-gold-deep",
                        r.estado_solicitud === "aceptado" && "bg-whatsapp/15 text-whatsapp",
                        r.estado_solicitud === "rechazado" && "bg-destructive/10 text-destructive",
                      )}
                    >
                      {r.estado_solicitud}
                    </span>
                  </div>

                  <div className="mt-4 space-y-2 rounded-2xl bg-muted/60 p-4 text-sm font-semibold text-foreground">
                    <p className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                      <span>
                        <strong>Origen:</strong> {r.origen}
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold-deep" aria-hidden />
                      <span>
                        <strong>Destino:</strong> {r.destino}
                      </span>
                    </p>
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {r.fecha_viaje} · {formatSlotLabel(r.hora_inicio.slice(0, 5))}
                      {r.ida_y_vuelta ? " · ida y vuelta" : ""}
                    </p>
                    {r.notas && <p className="text-xs font-semibold text-muted-foreground">Notas: {r.notas}</p>}
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-extrabold text-foreground">
                      {r.distancia_km ?? 0} km ·{" "}
                      <span className="text-gold-deep">{formatMXN(Number(r.precio_mxn ?? 0))}</span>
                    </p>
                    <p className="flex items-center gap-2 text-xs font-bold text-muted-foreground">
                      <PagoIcon className="h-4 w-4 text-primary" aria-hidden />
                      {pago.label} ·{" "}
                      <span className={r.pago_estado === "verificado" ? "text-whatsapp" : "text-gold-deep"}>
                        {r.pago_estado === "verificado" ? "Verificado" : "Pendiente"}
                      </span>
                    </p>
                  </div>

                  {pendiente && (
                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        disabled={busyId === r.id}
                        onClick={() => void run(r.id, "aceptar")}
                        className="flex items-center justify-center gap-2 rounded-full bg-whatsapp px-6 py-3.5 text-sm font-extrabold text-whatsapp-foreground shadow-soft transition-all hover:-translate-y-0.5 disabled:opacity-60"
                      >
                        {busyId === r.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        ) : (
                          <Check className="h-4 w-4" aria-hidden />
                        )}
                        Aceptar viaje
                      </button>
                      <button
                        type="button"
                        disabled={busyId === r.id}
                        onClick={() => void run(r.id, "rechazar")}
                        className="flex items-center justify-center gap-2 rounded-full bg-destructive/10 px-6 py-3.5 text-sm font-extrabold text-destructive transition-all hover:-translate-y-0.5 disabled:opacity-60"
                      >
                        <X className="h-4 w-4" aria-hidden />
                        Rechazar / Cancelar
                      </button>
                    </div>
                  )}

                  {r.pago_estado !== "verificado" && r.metodo_pago !== "efectivo" && (
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => void run(r.id, "verificar_pago")}
                      className="mt-3 w-full rounded-full bg-secondary px-6 py-3 text-sm font-extrabold text-primary transition-all hover:-translate-y-0.5 disabled:opacity-60"
                    >
                      Marcar pago como verificado
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        )}

        <Link
          to="/"
          className="mt-10 block text-center text-xs font-bold text-muted-foreground hover:text-primary"
        >
          Volver al sitio
        </Link>
      </div>
    </main>
  );
}
